import { useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgPen, CgTrash } from "react-icons/cg"; // icons for edit/delete
import { Modal } from "react-bootstrap";
import DeleteConfirmModal from "../Commaon/DeleteConfirmModal";
import ListHeader from "../Commaon/ListHeader";
import ListPagination from "../Commaon/ListPagination";
import LabTechnicianDetailsModal from "../LabTechnicians/LabTechnicianDetailsModal";

const EMPTY_FORM = {
  name: "",
  city: "",
  pincode: "",
  latitude: "",
  longitude: "",
  radiusKm: "",
};

const toInputValue = (value) =>
  value === null || value === undefined ? "" : String(value);

const isBlank = (value) => String(value ?? "").trim() === "";

// Mirrors the backend validation for service locations
const validateLocationForm = (form) => {
  const errors = {};
  if (isBlank(form.name)) errors.name = "Town / village name is required";
  if (isBlank(form.city)) errors.city = "City is required";
  if (!/^\d{6}$/.test(String(form.pincode).trim())) {
    errors.pincode = "Pincode must be exactly 6 digits";
  }

  const lat = Number(form.latitude);
  if (isBlank(form.latitude) || !Number.isFinite(lat)) {
    errors.latitude = "Latitude is required and must be a number";
  } else if (lat < -90 || lat > 90) {
    errors.latitude = "Latitude must be between -90 and 90";
  }

  const lng = Number(form.longitude);
  if (isBlank(form.longitude) || !Number.isFinite(lng)) {
    errors.longitude = "Longitude is required and must be a number";
  } else if (lng < -180 || lng > 180) {
    errors.longitude = "Longitude must be between -180 and 180";
  }

  const radius = Number(form.radiusKm);
  if (isBlank(form.radiusKm) || !Number.isFinite(radius)) {
    errors.radiusKm = "Radius is required and must be a number";
  } else if (radius <= 0 || radius > 100) {
    errors.radiusKm = "Radius must be greater than 0 and at most 100 km";
  }
  return errors;
};

const buildLocationPayload = (form) => ({
  name: form.name.trim(),
  city: form.city.trim(),
  pincode: String(form.pincode).trim(),
  latitude: Number(form.latitude),
  longitude: Number(form.longitude),
  radiusKm: Number(form.radiusKm),
});

const formatCoord = (value) =>
  value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
    ? "-"
    : Number(value).toFixed(5);

const CitiesList = () => {
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [addModel, setAddModel] = useState(false);
  const [editModel, setEditModel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [initialEditForm, setInitialEditForm] = useState(EMPTY_FORM);
  const [touched, setTouched] = useState({});
  const [editId, setEditId] = useState(null);
  const [editConfigured, setEditConfigured] = useState(true);
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [viewModel, setViewModel] = useState(false);
  const [viewCity] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteModel, setBulkDeleteModel] = useState(false);
  // Technicians in a service location (opened from the Agents count)
  const [agentsCity, setAgentsCity] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [viewTechnician, setViewTechnician] = useState(null);

  // search + pagination states
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);
  const getCities = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/city/getall`, {
        method: "GET",
      });
      const data = response.data;
      // The API answers "not found" when there are no locations left
      const rows = data.s === 200 ? data.r || [] : [];
      setCities(rows);
      setFilteredCities(rows);
    } catch (err) {
      console.error("Failed to fetch cities:", err);
      setSnack({
          notification: { message: "Error fetching service locations", type: "error" },
      });
      setCities([]);
      setFilteredCities([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getCities();
  }, []);

  const openAgents = async (city) => {
    setAgentsCity(city);
    setAgents([]);
    setAgentsLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/city/${city._id}/agents`, {
        method: "GET",
      });
      const data = response.data;
      if (data.s === 200) {
        setAgents(data.r || []);
      } else {
        setSnack({
          notification: { message: data.m || "Error fetching lab technicians", type: "error" },
        });
      }
    } catch (err) {
      setSnack({
        notification: { message: "Error fetching lab technicians", type: "error" },
      });
    } finally {
      setAgentsLoading(false);
    }
  };

  // search filter
  useEffect(() => {
    if (!query.trim()) {
      setFilteredCities(cities);
    } else {
      const q = query.trim().toLowerCase();
      setFilteredCities(
        cities.filter((c) =>
          [c.name, c.city, c.pincode].some((field) =>
            String(field ?? "").toLowerCase().includes(q)
          )
        )
      );
    }
    setPageNo(1); // reset to first page on search
  }, [query, cities]);

  // pagination slice
  const startIndex = (pageNo - 1) * limit;
  const paginatedCities = filteredCities.slice(startIndex, startIndex + limit);

  // action handlers
  const formErrors = validateLocationForm(form);
  const isFormValid = Object.keys(formErrors).length === 0;

  const handleFieldChange = (field) => (e) => {
    const { value } = e.target;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFieldBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setInitialEditForm(EMPTY_FORM);
    setTouched({});
  };

  const touchAllFields = () => {
    setTouched(
      Object.keys(EMPTY_FORM).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    );
  };

  const closeAddModal = () => {
    setAddModel(false);
    resetForm();
  };

  const closeEditModal = () => {
    setEditModel(false);
    setEditId(null);
    setEditConfigured(true);
    resetForm();
  };

  const editCity = (id) => {
    const city = cities.find((c) => c._id === id);
    if (city) {
      const prefilled = {
        name: toInputValue(city.name),
        city: toInputValue(city.city),
        pincode: toInputValue(city.pincode),
        latitude: toInputValue(city.latitude),
        longitude: toInputValue(city.longitude),
        radiusKm: toInputValue(city.radiusKm),
      };
      setEditId(id);
      setForm(prefilled);
      setInitialEditForm(prefilled);
      setEditConfigured(city.isConfigured !== false);
      setTouched({});
      setEditModel(true);
    }
  };

  const deleteCity = (id) => {
    setDeleteModel(true);
    setDeleteId(id);
  };

  const toggleCityStatus = async (city) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/city/${city._id}/toggle`, {
        method: "PATCH",
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        setSnack({
          notification: { message: "Service location status updated", type: "success" },
        });
        getCities();
      }
    } catch (err) {
      setSnack({
        notification: { message: "Error updating service location status", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  const addCity = () => {
    resetForm();
    setAddModel(true);
  };

  const visibleIds = paginatedCities.map((city) => city._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const runBulkAction = async (action, confirmed = false) => {
    if (!selectedIds.length) {
      setSnack({
        notification: { message: "Select at least one service location", type: "error" },
      });
      return;
    }

    if (action === "delete" && !confirmed) {
      setBulkDeleteModel(true);
      return;
    }

    try {
      setLoading(true);
      await Promise.all(
        selectedIds.map((id) => {
          if (action === "delete") {
            return AuthInterceptor(`/admin/city/${id}`, { method: "DELETE" });
          }
          const city = cities.find((item) => item._id === id);
          if (city?.isActive === (action === "activate")) return Promise.resolve();
          return AuthInterceptor(`/admin/city/${id}/toggle`, { method: "PATCH" });
        })
      );
      setSnack({
        notification: { message: "Bulk action completed", type: "success" },
      });
      setSelectedIds([]);
      getCities();
    } catch (error) {
      setSnack({
        notification: { message: "Bulk action failed", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  // API calls for add, edit, delete
  const handleAddCity = async (e) => {
    e.preventDefault();
    if (!isFormValid) {
      touchAllFields();
      setSnack({
        notification: { message: "Please fix the highlighted fields", type: "error" },
      });
      return;
    }

    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/city/add`, {
        method: "POST",
        body: JSON.stringify(buildLocationPayload(form)),
      });
      const data = response.data || {};

      if (data.s === 201 || data.s === 200) {
        setSnack({
          notification: { message: "Service location added successfully", type: "success" },
        });
        closeAddModal();
        getCities();
      } else {
        // keep the modal open so the admin can correct the input
        throw new Error(data.m || data.message || "Failed to add service location");
      }
    } catch (err) {
      console.error("Error adding city:", err);
      setSnack({
        notification: {
          message: err.message || "Error adding service location",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCity = async (e) => {
    e.preventDefault();
    if (!isFormValid) {
      touchAllFields();
      setSnack({
        notification: { message: "Please fix the highlighted fields", type: "error" },
      });
      return;
    }
    const unchanged = Object.keys(EMPTY_FORM).every(
      (key) => String(form[key]).trim() === String(initialEditForm[key]).trim()
    );
    if (unchanged) {
      setSnack({
        notification: { message: "Service location is not changed", type: "alert" },
      });
      return;
    }
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/city/${editId}`, {
        method: "PUT",
        body: JSON.stringify(buildLocationPayload(form)),
      });
      const data = response.data || {};

      if (data.s === 200) {
        setSnack({
          notification: {
            message: "Service location updated successfully",
            type: "success",
          },
        });
        closeEditModal();
        getCities();
      } else {
        // keep the modal open so the admin can correct the input
        throw new Error(data.m || data.message || "Failed to update service location");
      }
    } catch (err) {
      console.error("Error updating city:", err);
      setSnack({
        notification: {
          message: err.message || "Error updating service location",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field, label, props = {}, hint = null) => {
    const showError = touched[field] && formErrors[field];
    return (
      <div className="mb-3">
        <label className="form-label" htmlFor={`location-${field}`}>
          {label}
        </label>
        <input
          id={`location-${field}`}
          type="text"
          className={`form-control ${showError ? "is-invalid" : ""}`}
          value={form[field]}
          onChange={handleFieldChange(field)}
          onBlur={handleFieldBlur(field)}
          disabled={loading}
          {...props}
        />
        {showError ? (
          <div className="invalid-feedback">{formErrors[field]}</div>
        ) : (
          hint && <div className="form-text">{hint}</div>
        )}
      </div>
    );
  };

  const renderLocationFields = () => (
    <>
      <div className="row">
        <div className="col-12 col-sm-6">
          {renderField("latitude", "Latitude", {
            placeholder: "e.g. 28.8725",
            inputMode: "decimal",
          })}
        </div>
        <div className="col-12 col-sm-6">
          {renderField("longitude", "Longitude", {
            placeholder: "e.g. 75.9144",
            inputMode: "decimal",
          })}
        </div>
      </div>
      <div className="form-text mb-3" style={{ marginTop: "-0.5rem" }}>
        Latitude / longitude should be the center point of the town or village.
      </div>
      <div className="row">
        <div className="col-12 col-sm-6">
          {renderField("name", "Town/Village", { placeholder: "e.g. Tosham" })}
        </div>
        <div className="col-12 col-sm-6">
          {renderField("city", "City", { placeholder: "e.g. Bhiwani" })}
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-sm-6">
          {renderField("pincode", "Pincode", {
            placeholder: "e.g. 127040",
            inputMode: "numeric",
            maxLength: 6,
          })}
        </div>
        <div className="col-12 col-sm-6">
          {renderField("radiusKm", "Radius (km)", {
            type: "number",
            min: "0",
            max: "100",
            step: "any",
            placeholder: "e.g. 3",
          })}
        </div>
      </div>
      <div className="form-text mb-3" style={{ marginTop: "-0.5rem" }}>
        Radius should cover the whole town area from the center, e.g. 2–3 km for a small village, more for a bigger town (max 100 km). Addresses outside this circle are not serviceable.
      </div>
    </>
  );

  const handleDeleteCity = async (id) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/city/${id}`, {
        method: "DELETE",
      });
      const data = response.data;

      if (data.s === 200) {
        // Reload so the per-location agent counts stay in the rows
        getCities();
        setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
        setSnack({
          notification: {
            message: "Service location deleted successfully",
            type: "success",
          },
        });
      } else {
        throw new Error(data.m || "Failed to delete service location");
      }
    } catch (err) {
      console.error("Error deleting city:", err);
      setSnack({
        notification: {
          message: err.message || "Error deleting city",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
      setDeleteModel(false);
      setDeleteId(null);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <ListHeader
          title="Service Locations"
          searchValue={query}
          searchPlaceholder="Search service locations..."
          onSearchChange={setQuery}
          limit={limit}
          onLimitChange={(size) => {
            setPageNo(1);
            setLimit(size);
          }}
        />

        {/* Bulk actions | Add */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {/* Bulk actions only make sense when there are rows to select */}
          {paginatedCities.length > 0 && (
            <>
              <span className="text-muted small">{selectedIds.length} selected</span>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("activate")}>
                Active
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("inactive")}>
                Inactive
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={() => runBulkAction("delete")}>
                Delete
              </button>
            </>
          )}
          <button
            className="btn btn-sm btn-outline-custom ms-auto"
            onClick={() => addCity()}
          >
            Add Service Location
          </button>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table className="table table-hover gap-2 align-middle" style={{ minWidth: "900px" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all service locations on this page"
                  />
                </th>
                <th style={{ width: "60px" }}>Sr.</th>
                <th>Town/Village</th>
                <th>City</th>
                <th>Pincode</th>
                <th>Lat / Long</th>
                <th>Radius (km)</th>
                <th title="Lab technicians whose lab location is inside this service location">Agents</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                paginatedCities.length > 0 &&
                paginatedCities.map((c, idx) => (
                  <tr key={c._id || idx}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(c._id)}
                        onChange={() => toggleRowSelection(c._id)}
                        aria-label={`Select ${c.name || "service location"}`}
                      />
                    </td>
                    <td>{startIndex + idx + 1}</td>
                    <td>
                      <div>{c.name || "-"}</div>
                      {c.isConfigured === false && (
                        <span
                          className="badge bg-warning text-dark mt-1"
                          title="This location has no coordinates/radius and is ignored for orders"
                        >
                          Incomplete – add coordinates
                        </span>
                      )}
                    </td>
                    <td>{c.city || "-"}</td>
                    <td>{c.pincode || "-"}</td>
                    <td className="text-nowrap">
                      {formatCoord(c.latitude) === "-" && formatCoord(c.longitude) === "-"
                        ? "-"
                        : `${formatCoord(c.latitude)}, ${formatCoord(c.longitude)}`}
                    </td>
                    <td>
                      {c.radiusKm === null || c.radiusKm === undefined || c.radiusKm === ""
                        ? "-"
                        : c.radiusKm}
                    </td>
                    <td className="text-nowrap">
                      {c.agentCount ? (
                        <button
                          type="button"
                          className="btn btn-link p-0 fw-semibold text-decoration-none"
                          onClick={() => openAgents(c)}
                          title="View lab technicians"
                        >
                          {c.agentCount}
                        </button>
                      ) : (
                        <div>0</div>
                      )}
                      <div className="text-muted small">{c.activeAgentCount ?? 0} active</div>
                    </td>
                    <td>
                      {c.isActive ? (
                        <span className="badge bg-success" role="button" onClick={() => toggleCityStatus(c)}>Active</span>
                      ) : (
                        <span className="badge bg-secondary" role="button" onClick={() => toggleCityStatus(c)}>Inactive</span>
                      )}
                    </td>
                    <td className="text-nowrap">
                      <button
                        className="btn btn-sm btn-outline-custom me-2"
                        onClick={() => editCity(c._id)}
                      >
                        <CgPen size={16} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteCity(c._id)}
                      >
                        <CgTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}

              {!loading && paginatedCities.length === 0 && (
                <tr>
                  <td colSpan="10" className="text-center text-muted py-4">
                    No service locations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          show={filteredCities.length > 0}
          pageNo={pageNo}
          totalPages={Math.ceil(filteredCities.length / limit) || 1}
          onPageChange={setPageNo}
        />
      </div>

      {/* Add City Modal */}
      <Modal show={addModel} onHide={closeAddModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Service Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleAddCity} noValidate>
            {renderLocationFields()}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !isFormValid}
            >
              {loading ? <Spinnerr show={loading} /> : "Add Service Location"}
            </button>
          </form>
        </Modal.Body>
      </Modal>
      {/* Edit City Modal */}
      <Modal show={editModel} onHide={closeEditModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Service Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleEditCity} noValidate>
            {!editConfigured && (
              <div className="alert alert-warning py-2 small">
                This service location is incomplete and is currently ignored for
                orders. Add city, pincode, center coordinates and radius to enable it.
              </div>
            )}
            {renderLocationFields()}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !isFormValid}
            >
              {loading ? <Spinnerr show={loading} /> : "Update Service Location"}
            </button>
          </form>
        </Modal.Body>
      </Modal>

      {/* Delete City Modal */}
      <DeleteConfirmModal
        show={deleteModel}
        count={1}
        singular="service location"
        plural="service locations"
        loading={loading}
        onConfirm={() => {
          handleDeleteCity(deleteId);
          setDeleteModel(false);
        }}
        onCancel={() => {
          setDeleteModel(false);
          setDeleteId(null);
        }}
      />
      <DeleteConfirmModal
        show={bulkDeleteModel}
        count={selectedIds.length}
        singular="service location"
        plural="service locations"
        loading={loading}
        onConfirm={() => {
          setBulkDeleteModel(false);
          runBulkAction("delete", true);
        }}
        onCancel={() => setBulkDeleteModel(false)}
      />

      <Modal show={viewModel} onHide={() => setViewModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Service Location Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="text-muted small">Location Name</div>
            <div className="fw-semibold">{viewCity?.name || "-"}</div>
          </div>
          <div>
            <div className="text-muted small">Status</div>
            <span className={`badge ${viewCity?.isActive ? "bg-success" : "bg-secondary"}`}>
              {viewCity?.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </Modal.Body>
      </Modal>

      {/* Hidden while a technician's details are open; shows again on close */}
      <Modal
        show={Boolean(agentsCity) && !viewTechnician}
        onHide={() => setAgentsCity(null)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Lab Technicians – {[agentsCity?.name, agentsCity?.city].filter(Boolean).join(", ")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "60px" }}>Sr.</th>
                  <th>Lab ID</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {agentsLoading && (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">Loading...</td>
                  </tr>
                )}
                {!agentsLoading && agents.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">No lab technicians found</td>
                  </tr>
                )}
                {!agentsLoading &&
                  agents.map((agent, idx) => (
                    <tr
                      key={agent._id || idx}
                      role="button"
                      onClick={() => setViewTechnician(agent)}
                      title="View lab technician details"
                    >
                      <td>{idx + 1}</td>
                      <td className="fw-semibold">{agent.labId || "-"}</td>
                      <td>{agent.fullName || "-"}</td>
                      <td>{agent.mobileNumber || "-"}</td>
                      <td>
                        <span className={`badge ${agent.isActive ? "bg-success" : "bg-secondary"} me-1`}>
                          {agent.isActive ? "Active" : "Inactive"}
                        </span>
                        {!agent.isApproved && (
                          <span className="badge bg-warning text-dark">Approval pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>

      <LabTechnicianDetailsModal technician={viewTechnician} onHide={() => setViewTechnician(null)} />
    </>
  );
};

export default CitiesList;
